import { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import "@/App.css";
import useLenis from "@/hooks/useLenis";
import Grain from "@/components/veedu/Grain";
import Cursor from "@/components/veedu/Cursor";
import Preloader from "@/components/veedu/Preloader";
import { AmbientAudioProvider } from "@/context/AmbientAudioContext";
import { ReservationProvider } from "@/context/ReservationContext";
import ComingSoonModal from "@/components/veedu/ComingSoonModal";
import Nav from "@/components/veedu/Nav";
import ChapterRail from "@/components/veedu/ChapterRail";
import Hero from "@/components/veedu/Hero";
import TransitionalDivider from "@/components/veedu/TransitionalDivider";
import Courtyard from "@/components/veedu/Courtyard";
import OurStory from "@/components/veedu/OurStory";
import MenuJourney from "@/components/veedu/MenuJourney";
import ChefIntro from "@/components/veedu/ChefIntro";
import Gallery from "@/components/veedu/Gallery";
import Verandah from "@/components/veedu/Verandah";
import ChefJomonPage from "@/pages/ChefJomonPage";
import AdminRoot from "@/pages/admin/AdminRoot";
import AdminLogin from "@/pages/admin/Login";
import AdminLayout from "@/components/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Users from "@/pages/admin/Users";
import Roles from "@/pages/admin/Roles";
import Events from "@/pages/admin/Events";
import EventTags from "@/pages/admin/EventTags";
import GalleryAdmin from "@/pages/admin/GalleryAdmin";
import Categories from "@/pages/admin/Categories";
import MenuItems from "@/pages/admin/MenuItems";
import MenuItemForm from "@/pages/admin/MenuItemForm";

const House = () => {
  useLenis();
  const [loading, setLoading] = useState(true);
  const [doorOpening, setDoorOpening] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (loading) return;
    const sections = Array.from(document.querySelectorAll("[data-chapter]"));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const idx = sections.indexOf(e.target);
            if (idx !== -1) setActive(idx);
          }
        });
      },
      { rootMargin: "-25% 0px -25% 0px" }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [loading]);

  return (
    <>
      <AnimatePresence>
        {loading && (
          <Preloader
            onStartOpening={() => setDoorOpening(true)}
            onDone={() => setLoading(false)}
          />
        )}
      </AnimatePresence>
      <Nav />
      <ChapterRail active={active} />
      <main>
        <Hero start={doorOpening || !loading} />
        <TransitionalDivider />
        <OurStory />
        <TransitionalDivider />
        <MenuJourney />
        <TransitionalDivider />
        <ChefIntro />
        <TransitionalDivider />
        <Courtyard />
        <TransitionalDivider />
        <Verandah />
      </main>
    </>
  );
};

const BackToTop = () => {
  const [show, setShow] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => setShow(window.scrollY > 800);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!show) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-6 right-6 z-50 flex h-10 w-10 items-center justify-center rounded-full border border-brass/50 bg-ink/90 text-brass shadow-xl backdrop-blur transition-all active:scale-95 md:hidden"
      aria-label="Back to top"
    >
      <span className="font-body text-[10px] tracking-[0.2em] font-medium">↑</span>
    </button>
  );
};

const Shell = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <AmbientAudioProvider>
      <ReservationProvider>
        <div className="App bg-ink text-cream">
          {!isAdmin && <Grain />}
          {!isAdmin && <Cursor />}
          {!isAdmin && <BackToTop />}
          {!isAdmin && <ComingSoonModal />}
          <Routes>
          <Route path="/" element={<House />} />
          <Route path="/chef-jomon" element={<ChefJomonPage />} />
          <Route path="/our-founder" element={<ChefJomonPage />} />
          <Route path="/admin" element={<AdminRoot />}>
            <Route path="login" element={<AdminLogin />} />
            <Route element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="users" element={<Users />} />
              <Route path="roles" element={<Roles />} />
              <Route path="events" element={<Events />} />
              <Route path="event-tags" element={<EventTags />} />
              <Route path="gallery" element={<GalleryAdmin />} />
              <Route path="categories" element={<Categories />} />
              <Route path="menu" element={<MenuItems />} />
              <Route path="menu/new" element={<MenuItemForm />} />
              <Route path="menu/:id/edit" element={<MenuItemForm />} />
            </Route>
          </Route>
        </Routes>
        </div>
      </ReservationProvider>
    </AmbientAudioProvider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}

export default App;
